<script setup>
/**
 * Do not contact — the numbers this workspace will not send to. THE PLAN, "The screens".
 *
 * This list is the single control protecting the business's WhatsApp sender rating: every
 * send the owner's own systems ask for is checked against it before it costs anything, and
 * a customer who was messaged after opting out is the complaint that damages the rating
 * Meta scores the number on. So the page's job is not "manage a list of strings" — it is
 * to make the difference between the four reasons legible, because two of them are the
 * workspace's to lift and two of them are the customer's.
 *
 * ── THREE THINGS THIS PAGE IS SHAPED BY ────────────────────────────────────────────────
 *
 * 1. THE REMOVE BUTTON IS OFFERED ONLY WHERE IT WOULD WORK. `keyword` and `provider` are
 *    refused upstream with 403 `opt_out_not_removable`; `SuppressionRow` reads
 *    `WASLO_SUPPRESSION_REMOVABLE_REASONS` and declines to draw a control for them. The
 *    refusal is still handled here — the list a browser is looking at can be seconds
 *    stale, and the API is the only authority — but the UI must never be the thing that
 *    provokes it. There is no typed-acknowledgement gate for lifting a customer's opt-out:
 *    that flow does not exist on either side of the wire, and building it would put a
 *    ceremony in front of an action the API refuses outright.
 *
 * 2. NOTHING IS NORMALISED IN THE BROWSER. `AddSuppressionForm` posts what was typed; the
 *    sidecar's `normaliseE164` is the one authority, and it refuses rather than repairs.
 *    The header of that component has the long version.
 *
 * 3. THE EMPTY STATE IS INLINE, NOT `WasloPageLayout`'s `isEmpty` BRANCH. That branch
 *    replaces the body slot — which would delete the add form at the exact moment it is
 *    the only thing worth pressing, and the filter toggle at the exact moment flipping it
 *    back is the answer. `ConnectionsPage` and `TasksPage` refuse it for the same reason.
 */
import { computed, ref, watch } from 'vue';

import { useAlert } from 'dashboard/composables';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

import AddSuppressionForm from '../../components/outbound/AddSuppressionForm.vue';
import OutboundNotSetUp from '../../components/outbound/OutboundNotSetUp.vue';
import SuppressionRow from '../../components/outbound/SuppressionRow.vue';
import WasloExplainer from '../../components/WasloExplainer.vue';
import WasloPageLayout from '../../components/WasloPageLayout.vue';
import { useWasloI18n } from '../../i18n.js';
import { useWasloApi } from '../../composables/useWasloApi.js';
import { useWasloAccountId } from '../../composables/useWasloAccountId.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { wasloApi, WASLO_ERROR_CODES } from '../../api/client.js';

/** The BFF's own default page size (`routes/api/outbound.ts`), asked for explicitly. */
const PAGE_SIZE = 50;

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();

const key = name => `WASLO.OUTBOUND.NUMBERS.${name}`;

// ── what is being looked at ────────────────────────────────────────────────────────
const includeRemoved = ref(false);
const offset = ref(0);

// `isEmpty: () => false` — this page decides its own emptiness below, and it decides it
// differently depending on the filter. The composable's default would hand the whole body
// to the layout's empty branch instead (see 3 in the header).
const numbers = useWasloApi(
  signal =>
    wasloApi.getOutboundNumbers(
      accountId.value,
      {
        includeRemoved: includeRemoved.value,
        limit: PAGE_SIZE,
        offset: offset.value,
      },
      signal
    ),
  { isEmpty: () => false }
);

// One watcher over both, so flipping the filter (which also resets the page) issues ONE
// read rather than two — Vue batches the two writes into a single callback.
watch([includeRemoved, offset], () => numbers.reload());

const rows = computed(() => numbers.data.value?.numbers ?? []);
const total = computed(() => numbers.data.value?.total ?? 0);

/**
 * "No WhatsApp number is connected yet" is a STATE, not a failure — the normal condition of
 * a workspace on its first day — and it arrives here as a 403 on the list read.
 *
 * This page was the last of the four to still render it as an error, so a brand-new
 * workspace opening Do-not-contact was told to check its connection about a connection that
 * was fine. That is the same defect `WASLO_STATE.NOT_PROVISIONED` exists to prevent one
 * layer up, and the same one `OUTBOUND_NOT_PROVISIONED` was promoted to a real wire code to
 * close on Setup and the Log.
 *
 * NOT to be confused with the other 403 this page can see: `confirmRemove` reads
 * `FORBIDDEN` for `opt_out_not_removable`, which is a refusal of one action rather than a
 * statement about the workspace, and correctly stays as it is.
 */
const isNotSetUp = computed(
  () => numbers.error.value?.code === WASLO_ERROR_CODES.OUTBOUND_NOT_PROVISIONED
);

// Blanked when the state above holds: `OutboundNotSetUp` is already saying it, in words
// that fit, and the frame's error branch would hide the body it renders into.
const errorMessage = computed(() =>
  isNotSetUp.value ? '' : numbers.errorMessage.value
);

const onFilter = next => {
  includeRemoved.value = next;
  offset.value = 0;
};

// ── the pager ──────────────────────────────────────────────────────────────────────
// Built from what the SERVER said it gave us, never from `PAGE_SIZE`: the two agree today
// and the difference between them is the entire point of a pager the day they do not.
const shownOffset = computed(() => numbers.data.value?.offset ?? 0);

const formatNumber = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

const showingLabel = computed(() =>
  t('WASLO.OUTBOUND.COMMON.PAGER.SHOWING', {
    from: formatNumber(shownOffset.value + 1),
    to: formatNumber(shownOffset.value + rows.value.length),
    total: formatNumber(total.value),
  })
);

const hasPrevious = computed(() => shownOffset.value > 0);
const hasNext = computed(
  () => shownOffset.value + rows.value.length < total.value
);

const goPrevious = () => {
  offset.value = Math.max(0, shownOffset.value - PAGE_SIZE);
};
const goNext = () => {
  offset.value = shownOffset.value + rows.value.length;
};

// ── adding ─────────────────────────────────────────────────────────────────────────
/**
 * `POST /suppressions` is idempotent upstream: a number already on the list comes back as
 * the row it already was, with its original `createdAt`, rather than as a new one. So the
 * answer to "was that an addition?" is whether we were already looking at that row — which
 * is worth saying out loud, because the alternative is telling an owner she has added
 * something when nothing changed.
 */
const onAdded = row => {
  const alreadyHere = rows.value.some(
    existing => existing.id === row.id && existing.removedAt === null
  );
  useAlert(alreadyHere ? t(key('ADD.DUPLICATE')) : t(key('ADD.ADDED')));
  // Newest first upstream, so a genuinely new row is on the first page and nowhere else.
  if (offset.value === 0) numbers.reload();
  else offset.value = 0;
};

// ── removing ───────────────────────────────────────────────────────────────────────
const removeDialogRef = ref(null);
const pending = ref(null);
const isRemoving = ref(false);
const removeFailure = ref('');

const askRemove = row => {
  pending.value = row;
  removeFailure.value = '';
  removeDialogRef.value?.open();
};

const onDialogClose = () => {
  pending.value = null;
  removeFailure.value = '';
};

const confirmRemove = async () => {
  const row = pending.value;
  if (row === null || isRemoving.value || accountId.value === null) return;
  isRemoving.value = true;
  removeFailure.value = '';
  try {
    await wasloApi.removeOutboundNumber(accountId.value, row.phone);
    removeDialogRef.value?.close();
    useAlert(t(key('REMOVE.DONE')));
    numbers.reload();
  } catch (error) {
    // The 403 the API answers for a customer's own opt-out. Reachable even though no
    // button was offered for one: the row could have become a `keyword` between the read
    // and the press. Our own sentence rather than the server's, so it is translated — the
    // BFF's wording is the same sentence in English.
    const refused = error?.code === WASLO_ERROR_CODES.FORBIDDEN;
    removeFailure.value = refused
      ? t(key('REMOVE.REFUSED'))
      : t(key('REMOVE.FAILED'));
    // Both of these mean the list on screen is out of date about this number, so re-read
    // it — the row's real reason is the thing that explains the refusal.
    if (refused || error?.code === WASLO_ERROR_CODES.NOT_FOUND)
      numbers.reload();
  } finally {
    isRemoving.value = false;
  }
};

// ── the two ways this page can be empty ────────────────────────────────────────────
// Only ever claimed once a read has actually answered.
const isEmpty = computed(
  () =>
    !numbers.isFetching.value && !errorMessage.value && rows.value.length === 0
);
</script>

<template>
  <WasloPageLayout
    :header-title="t(key('TITLE'))"
    :is-fetching="numbers.isFetching.value"
    :not-provisioned="numbers.isNotProvisioned.value"
    :error-message="errorMessage"
    @retry="numbers.reload()"
  >
    <template #body>
      <!-- Nothing is connected, so there is no list to show and nothing an Add form could
           usefully write to. The explainer goes with it: a paragraph about which numbers
           this workspace will not message is noise above "you have no numbers yet". The
           same component says this on Setup, on the Log and on one message. -->
      <OutboundNotSetUp v-if="isNotSetUp" />

      <div v-else class="flex flex-col gap-8 pb-10">
        <WasloExplainer
          icon="i-lucide-user-round-x"
          :note="t(key('SPOTLIGHT'))"
        />

        <!-- 1 · ADD. First, because it is the page's one write and the empty state has
             nothing else to offer. -->
        <section class="flex flex-col gap-4" data-waslo-numbers-add-section>
          <SettingsHeader :heading="t(key('ADD.HEADING'))" />
          <div
            class="w-full px-5 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
          >
            <AddSuppressionForm :account-id="accountId" @added="onAdded" />
          </div>
        </section>

        <!-- 2 · THE LIST. -->
        <section class="flex flex-col gap-4" data-waslo-numbers-section>
          <SettingsHeader
            :heading="t(key('HEADING'))"
            :description="t(key('DESCRIPTION'))"
          />

          <label
            class="flex items-center self-start gap-2 px-3 py-2 rounded-lg cursor-pointer bg-n-alpha-1 hover:bg-n-alpha-2"
            data-waslo-numbers-filter
          >
            <Checkbox
              :model-value="includeRemoved"
              @update:model-value="onFilter"
            />
            <span class="text-sm text-n-slate-12">
              {{ t(key('SHOW_REMOVED')) }}
            </span>
          </label>

          <ul
            v-if="rows.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-numbers-list
          >
            <SuppressionRow
              v-for="row in rows"
              :key="row.id"
              :row="row"
              :is-removing="isRemoving && pending?.id === row.id"
              :is-busy="isRemoving"
              @remove="askRemove"
            />
          </ul>

          <!--
            TWO EMPTY STATES, AND THEY ARE DIFFERENT FACTS.

            With the filter off, the list IS the numbers we will not message, and there
            being none of them is the ordinary first-run answer — so it says how numbers
            arrive here on their own.

            With the filter ON, the reader has just asked a narrower question ("show me the
            ones that came off too") and the honest answer to that question is that none
            ever did. Same screen, opposite readings; a single "nothing here" would answer
            neither.
          -->
          <div
            v-else-if="isEmpty"
            class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-numbers-empty
            :data-waslo-numbers-empty-kind="
              includeRemoved ? 'filtered' : 'none'
            "
          >
            <span
              class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-alpha-2"
              aria-hidden="true"
            >
              <span class="size-5 i-lucide-user-round-check text-n-slate-10" />
            </span>
            <div class="flex flex-col gap-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{
                  includeRemoved
                    ? t(key('EMPTY.FILTERED_TITLE'))
                    : t(key('EMPTY.TITLE'))
                }}
              </p>
              <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                {{
                  includeRemoved
                    ? t(key('EMPTY.FILTERED_BODY'))
                    : t(key('EMPTY.BODY'))
                }}
              </p>
            </div>
          </div>

          <!--
            The pager. Rendered whenever there is anything at all, including on the last
            page, so the count is always readable — and Previous stays live on a page that
            has gone empty underneath the reader (rows removed by someone else), which is
            the one way an offset pager can strand you.
          -->
          <div
            v-if="rows.length || hasPrevious"
            class="flex flex-wrap items-center justify-between gap-3"
            data-waslo-numbers-pager
          >
            <span class="text-sm text-n-slate-11" data-waslo-numbers-showing>
              {{
                rows.length
                  ? showingLabel
                  : t('WASLO.OUTBOUND.COMMON.PAGER.NONE')
              }}
            </span>
            <div class="flex items-center gap-2">
              <Button
                :label="t('WASLO.OUTBOUND.COMMON.PAGER.PREVIOUS')"
                variant="faded"
                color="slate"
                size="sm"
                :disabled="!hasPrevious"
                data-waslo-numbers-previous
                @click="goPrevious"
              />
              <Button
                :label="t('WASLO.OUTBOUND.COMMON.PAGER.NEXT')"
                variant="faded"
                color="slate"
                size="sm"
                :disabled="!hasNext"
                data-waslo-numbers-next
                @click="goNext"
              />
            </div>
          </div>
        </section>
      </div>
    </template>
  </WasloPageLayout>

  <!-- Outside the frame: it teleports to <body> and must not be unmounted by the state
       branches inside the layout. ONE dialog for the page, not one per row. -->
  <Dialog
    ref="removeDialogRef"
    type="alert"
    width="md"
    :title="t(key('REMOVE.CONFIRM_TITLE'))"
    :description="t(key('REMOVE.CONFIRM_BODY'))"
    :confirm-button-label="t(key('REMOVE.CONFIRM_YES'))"
    :cancel-button-label="t(key('REMOVE.CANCEL'))"
    :is-loading="isRemoving"
    :disable-confirm-button="isRemoving"
    data-waslo-numbers-remove-dialog
    @confirm="confirmRemove"
    @close="onDialogClose"
  >
    <div class="flex flex-col gap-4">
      <Banner
        v-if="removeFailure"
        color="ruby"
        data-waslo-numbers-remove-failure
      >
        {{ removeFailure }}
      </Banner>
      <!-- WHICH number, in an isolate. The confirmation is worth nothing if it does not
           name the thing it is confirming. -->
      <p
        v-if="pending"
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-numbers-remove-phone
      >
        <bdi>{{ pending.phone }}</bdi>
      </p>
    </div>
  </Dialog>
</template>
